import { useQuery } from '@tanstack/react-query';
import { getDiscordSdk, ACTIVITY_OAUTH_SCOPES } from './sdk-client';
import { resolveActivitySession } from './api';
import type { DiscordActivityResolveResponse } from '@runhuman/shared';

export const ACTIVITY_SESSION_QUERY_KEY = 'discord-activity-session';

/**
 * Five minutes — long enough that ordinary window-focus churn during an
 * Activity session can't burn through Discord's per-app rate limit on
 * `POST /oauth2/token`, short enough that any cache staleness self-heals on
 * the next focus event after the window passes (#4145). The link-handoff
 * recovery from #4035 does NOT rely on this expiring — it uses an explicit
 * `invalidateQueries` call (see comment in `useActivitySession` below).
 */
const SESSION_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Drive the SDK auth handshake → backend role resolution → cached session.
 *
 * Flow on first mount:
 *   1. `sdk.ready()` waits for the Discord client to attach to the iframe.
 *   2. `sdk.commands.authorize(...)` returns an OAuth code (the user sees
 *      Discord's standard scope-consent dialog the first time).
 *   3. `resolveActivitySession({ code })` exchanges + looks up roles + returns
 *      `{ view, discordUserId }`.
 *   4. `sdk.commands.authenticate({ access_token })` is NOT called here
 *      because we don't pass the access token back to the iframe — the backend
 *      keeps it server-side (the iframe never needs to make Discord-API calls
 *      on its own; everything proxied through our backend).
 *
 * The query is keyed on the SDK's `instanceId` so a user re-launching the
 * Activity in a different voice channel re-runs the handshake (each instance
 * is a fresh Discord-issued context).
 *
 * Cache freshness:
 *   - `staleTime: SESSION_STALE_TIME_MS` suppresses the implicit refetch that
 *     `refetchOnWindowFocus` (default `true`) would otherwise fire on every
 *     Alt-Tab. Each refetch re-runs `sdk.commands.authorize` and the backend
 *     token exchange against Discord's `/oauth2/token` endpoint, which is
 *     heavily rate-limited per OAuth client_id — without a stale window, a
 *     user who toggles focus a few times in a few minutes can trip the limit
 *     and see "You are being rate limited for requesting too many tokens"
 *     (#4145).
 *   - The link-handoff recovery from #4035 (user links Discord on web, comes
 *     back to the Activity, view flips from `unlinked` to `tester`/`admin`)
 *     still works because `<UnlinkedView>` and `<ActivityIdentityHeader>`
 *     call `queryClient.invalidateQueries([ACTIVITY_SESSION_QUERY_KEY])` at
 *     the moment of handoff. Invalidating marks the entry stale regardless
 *     of `staleTime`, so the next focus event (or the explicit "check again"
 *     button) does refetch.
 *
 * `prompt: 'none'` keeps subsequent authorize calls silent — re-resolves
 * never re-prompt the user once they've granted scope on first mount.
 */
export function useActivitySession() {
  const sdk = getDiscordSdk();
  return useQuery<DiscordActivityResolveResponse, Error>({
    queryKey: [ACTIVITY_SESSION_QUERY_KEY, sdk.instanceId],
    queryFn: async () => {
      await sdk.ready();
      const { code } = await sdk.commands.authorize({
        client_id: sdk.clientId,
        response_type: 'code',
        state: '',
        prompt: 'none',
        scope: ACTIVITY_OAUTH_SCOPES as unknown as Parameters<
          typeof sdk.commands.authorize
        >[0]['scope'],
      });
      return resolveActivitySession({ code });
    },
    staleTime: SESSION_STALE_TIME_MS,
    retry: false,
  });
}
